import React from 'react';
import useFitText from 'use-fit-text';
import {Report} from '../../../../types/Api/Reports';
import css from './ReportsV2ViewStatistics.module.scss';
import {StatisticsItem} from '../../ReportsV2StatisticsItem/ReportsV2StatisticsItem';
import {IconHeart} from '../../../../system/icons/IconHeart';
import {IconLightning} from '../../../../system/icons/IconLightning';
import {IconFire} from '../../../../system/icons/IconFire';
import {
  ReportsV2IntensityIcon,
  getIntensityLabel,
} from '../../ReportsV2List/ReportsV2ListIntensity/ReportsV2ListIntensity';
import moment from 'moment';
import {IconTrendingUp} from '../../../../system/icons/IconTrendingUp';
import {IconVisibility} from '../../../../system/icons/IconVisibility';
import {IconPlayCircle} from '../../../../system/icons/IconPlayCircle';
import {IconPersonAdd} from '../../../../system/icons/IconPersonAdd';
import {IconStreamsCharts} from '../../../../system/icons/IconStreamsCharts';

type ReportsV2ViewStatisticsProps = {
  report?: Report;
};

export const ReportsV2ViewStatistics = ({
  report,
}: ReportsV2ViewStatisticsProps) => {
  const isLoading = !report;
  const shouldShowSecondRow =
    !isLoading &&
    (report?.statistics?.peakViewers || report?.statistics?.watched);

  return (
    <div className={css.statisticsWrapper}>
      <div className={css.statistics}>
        <StatisticsItem
          loading={isLoading}
          title="Max heart rate"
          value={report?.statistics?.max}
          valueSubText="BPM"
          titleIcon={<IconLightning className={css.maxRateIcon} />}
        />

        <StatisticsItem
          loading={isLoading}
          title="Avg heart rate"
          value={report?.statistics?.avg}
          valueSubText="BPM"
          titleIcon={<IconHeart className={css.avgRateIcon} />}
        />

        <StatisticsItem
          loading={isLoading}
          title="Calories burned"
          value={report?.statistics?.calories}
          valueSubText="KCAL"
          titleIcon={<IconFire className={css.caloriesIcon} />}
        />

        <StatisticsItem
          loading={isLoading}
          title="Intensity"
          value={
            <IntensityItemValue intensity={report?.statistics?.intensity!} />
          }
        />

        {shouldShowSecondRow && (
          <>
            <StatisticsItem
              loading={isLoading}
              title="Peak viewers"
              value={report?.statistics?.peakViewers}
              titleIcon={<IconTrendingUp className={css.icon} />}
            />

            <StatisticsItem
              loading={isLoading}
              title="Average viewers"
              value={report?.statistics?.averageViewers}
              titleIcon={<IconVisibility className={css.icon} />}
            />

            <StatisticsItem
              loading={isLoading}
              title="Total watched"
              value={`${moment
                .duration(report?.statistics?.watched)
                .asHours()} hours`}
              titleIcon={<IconPlayCircle className={css.icon} />}
            />

            <StatisticsItem
              loading={isLoading}
              title="Followers gain"
              value={report?.statistics?.followersGain}
              titleIcon={<IconPersonAdd className={css.icon} />}
            />
          </>
        )}
      </div>
      {shouldShowSecondRow && (
        <div className={css.poweredByFooter}>
          Viewership data by
          <a
            className={css.poweredByFooterLink}
            href="https://streamscharts.com/channels?utm_campaign=pulsoid&utm_medium=pulsoid_ref"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconStreamsCharts className={css.poweredByFooterIcon} />
            StreamsCharts.com
          </a>
        </div>
      )}
    </div>
  );
};

const IntensityItemValue = ({intensity}: {intensity: number}) => {
  const [isIntensityVisible, setIsIntensityVisible] = React.useState(false);
  const {fontSize, ref} = useFitText({
    maxFontSize: 100,
    minFontSize: 20,
    resolution: 4,
    onFinish: () => setIsIntensityVisible(true),
  });

  return (
    <div className={css.intensity}>
      <ReportsV2IntensityIcon
        intensity={intensity}
        width={120}
        className={css.intensityIcon}
      />

      <div
        className={css.intensityText}
        style={{
          fontSize,
          opacity: isIntensityVisible ? 1 : 0,
        }}
        ref={ref}
      >
        {getIntensityLabel(intensity)}
      </div>
    </div>
  );
};
